import { useFormContext, Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { DatePicker } from "../DatePicker";
import { FormField } from "../FormField";
import { Asterisk } from "lucide-react";
import { ResidentFormData } from "../../schemas/residentForm";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface ResidentIndividualFormProps {
  arrayIndex: number;
  disabled?: boolean;
  isPpe?: boolean;
}

export const ResidentIndividualForm: React.FC<ResidentIndividualFormProps> = ({
  arrayIndex,
  disabled = false,
  isPpe = false,
}) => {
  const { t } = useTranslation();
  const {
    register,
    control,
    watch,
    setValue,
    formState: { errors },
  } = useFormContext<ResidentFormData>();
  const tenantErrors = errors.individual_tenants?.[arrayIndex];

  return (
    <div className="flex flex-col gap-4 sm:gap-6 md:gap-[24px]">
      <p className="font-bold text-off-black text-base sm:text-lg md:text-[20px]">
        {t("form.identity.title")}
      </p>

      <div className="flex flex-col gap-4 sm:gap-6 md:gap-[24px]">
        {/* Civilité */}
        <FormField
          label={t("form.identity.civilite")}
          required
          error={tenantErrors?.civilite?.message}
        >
          <Controller
            name={`individual_tenants.${arrayIndex}.civilite`}
            control={control}
            render={({ field }) => (
              <Select
                value={field.value || ""}
                onValueChange={(value) => {
                  field.onChange(value);
                  field.onBlur();
                }}
                disabled={disabled}
              >
                <SelectTrigger
                  data-testid="civilite-select"
                  className="h-[48px] px-[14px] py-[10px] border border-[#d0d5dd] rounded-[8px] focus:outline-none focus:ring-2 focus:ring-primary text-base text-[#757288] w-full"
                >
                  <SelectValue placeholder={t("form.identity.civilite")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="monsieur">
                    {t("form.identity.civiliteOptions.monsieur")}
                  </SelectItem>
                  <SelectItem value="madame">
                    {t("form.identity.civiliteOptions.madame")}
                  </SelectItem>
                  <SelectItem value="ne_souhaite_pas_communiquer">
                    {t("form.identity.civiliteOptions.ne_souhaite_pas_communiquer")}
                  </SelectItem>
                </SelectContent>
              </Select>
            )}
          />
        </FormField>

        {/* Nom and Prénom */}
        <div className="flex flex-col sm:flex-row gap-4 sm:gap-[16px]">
          <FormField
            label={t("form.identity.lastName")}
            required
            error={tenantErrors?.nom?.message}
            className="flex-1"
          >
            <input
              type="text"
              {...register(`individual_tenants.${arrayIndex}.nom`)}
              placeholder={t("form.placeholders.lastName")}
              disabled={disabled}
              className="h-[48px] px-[14px] py-[10px] border border-[#d0d5dd] rounded-[8px] focus:outline-none focus:ring-2 focus:ring-primary text-base text-[#757288]"
            />
          </FormField>
          <FormField
            label={t("form.identity.firstName")}
            required
            error={tenantErrors?.prenom?.message}
            className="flex-1"
          >
            <input
              type="text"
              {...register(`individual_tenants.${arrayIndex}.prenom`)}
              placeholder={t("form.placeholders.firstName")}
              disabled={disabled}
              className="h-[48px] px-[14px] py-[10px] border border-[#d0d5dd] rounded-[8px] focus:outline-none focus:ring-2 focus:ring-primary text-base text-[#757288]"
            />
          </FormField>
        </div>

        {/* Date de naissance and Nationalité - hidden for PPE */}
        {!isPpe && (
          <div className="flex flex-col sm:flex-row gap-4 sm:gap-[16px]">
            <FormField
              label={t("form.identity.birthDate")}
              required
              error={tenantErrors?.date_naissance?.message}
              className="flex-1"
            >
              <Controller
                name={`individual_tenants.${arrayIndex}.date_naissance`}
                control={control}
                render={({ field }) => (
                  <DatePicker
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    name={field.name}
                    placeholder={t("form.placeholders.birthDate")}
                    disabled={disabled}
                  />
                )}
              />
            </FormField>
            <FormField
              label={t("form.identity.nationality")}
              required
              error={tenantErrors?.nationalite?.message}
              className="flex-1"
            >
              <Controller
                name={`individual_tenants.${arrayIndex}.nationalite`}
                control={control}
                render={({ field }) => {
                  const validValues = ["suisse", "autre"];
                  const fieldValue = field.value?.trim() || "";
                  const currentValue = validValues.includes(fieldValue) ? fieldValue : "";

                  return (
                    <Select
                      value={currentValue}
                      onValueChange={(value) => {
                        field.onChange(value);
                        field.onBlur();
                      }}
                      disabled={disabled}
                    >
                      <SelectTrigger
                        data-testid="nationality-select"
                        className="h-[48px] px-[14px] py-[10px] border border-[#d0d5dd] rounded-[8px] focus:outline-none focus:ring-2 focus:ring-primary text-base text-[#757288] w-full"
                      >
                        <SelectValue
                          placeholder={<span>{t("form.placeholders.nationality")}</span>}
                        />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="suisse">
                          <span>{t("form.identity.nationalityOptions.suisse")}</span>
                        </SelectItem>
                        <SelectItem value="autre">
                          <span>{t("form.identity.nationalityOptions.autre")}</span>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  );
                }}
              />
            </FormField>
          </div>
        )}

        {/* Email and Tél. portable */}
        <div className="flex flex-col sm:flex-row gap-4 sm:gap-[16px]">
          <FormField
            label={t("form.identity.email")}
            required
            error={tenantErrors?.email?.message}
            className="flex-1"
          >
            <input
              type="email"
              {...register(`individual_tenants.${arrayIndex}.email`)}
              placeholder={t("form.placeholders.email")}
              disabled={disabled}
              className="h-[48px] px-[14px] py-[10px] border border-[#d0d5dd] rounded-[8px] focus:outline-none focus:ring-2 focus:ring-primary text-base text-[#757288]"
            />
          </FormField>
          <FormField
            label={t("form.identity.mobilePhone")}
            error={tenantErrors?.tel_portable?.message}
            className="flex-1"
          >
            <input
              type="tel"
              {...register(`individual_tenants.${arrayIndex}.tel_portable`)}
              placeholder={t("form.placeholders.phone")}
              disabled={disabled}
              className="h-[48px] px-[14px] py-[10px] border border-[#d0d5dd] rounded-[8px] focus:outline-none focus:ring-2 focus:ring-primary text-base text-[#757288]"
            />
          </FormField>
        </div>

        {/* Tél. professionnel and Téléphone */}
        <div className="flex flex-col sm:flex-row gap-4 sm:gap-[16px]">
          <FormField
            label={t("form.identity.workPhone")}
            error={tenantErrors?.tel_professionnel?.message}
            className="flex-1"
          >
            <input
              type="tel"
              {...register(`individual_tenants.${arrayIndex}.tel_professionnel`)}
              placeholder={t("form.placeholders.workPhone")}
              disabled={disabled}
              className="h-[48px] px-[14px] py-[10px] border border-[#d0d5dd] rounded-[8px] focus:outline-none focus:ring-2 focus:ring-primary text-base text-[#757288]"
            />
          </FormField>
          <FormField
            label={t("form.identity.phone")}
            error={tenantErrors?.telephone?.message}
            className="flex-1"
          >
            <input
              type="tel"
              {...register(`individual_tenants.${arrayIndex}.telephone`)}
              placeholder={t("form.placeholders.phone")}
              disabled={disabled}
              className="h-[48px] px-[14px] py-[10px] border border-[#d0d5dd] rounded-[8px] focus:outline-none focus:ring-2 focus:ring-primary text-base text-[#757288]"
            />
          </FormField>
        </div>

        {/* Correspondence Address */}
        <div className="flex flex-col gap-4 sm:gap-[16px] w-full">
          <p className="font-bold text-off-black text-base">
            {t("form.identity.correspondenceAddress")}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 sm:gap-[16px]">
            <FormField
              label={t("form.identity.street")}
              error={tenantErrors?.rue?.message}
              className="flex-1"
            >
              <input
                type="text"
                {...register(`individual_tenants.${arrayIndex}.rue`)}
                placeholder={t("form.placeholders.street")}
                disabled={disabled}
                className="h-[48px] px-[14px] py-[10px] border border-[#d0d5dd] rounded-[8px] focus:outline-none focus:ring-2 focus:ring-primary text-base text-[#757288]"
              />
            </FormField>
            <FormField
              label={t("form.identity.number")}
              error={tenantErrors?.numero?.message}
              className="flex-1"
            >
              <input
                type="text"
                {...register(`individual_tenants.${arrayIndex}.numero`)}
                placeholder={t("form.placeholders.number")}
                disabled={disabled}
                className="h-[48px] px-[14px] py-[10px] border border-[#d0d5dd] rounded-[8px] focus:outline-none focus:ring-2 focus:ring-primary text-base text-[#757288]"
              />
            </FormField>
          </div>
          <div className="flex flex-col sm:flex-row gap-4 sm:gap-[16px]">
            <FormField
              label={t("form.identity.postalCode")}
              error={tenantErrors?.npa?.message}
              className="flex-1"
            >
              <input
                type="number"
                {...register(`individual_tenants.${arrayIndex}.npa`, { valueAsNumber: true })}
                placeholder={t("form.placeholders.postalCode")}
                disabled={disabled}
                className="h-[48px] px-[14px] py-[10px] border border-[#d0d5dd] rounded-[8px] focus:outline-none focus:ring-2 focus:ring-primary text-base text-[#757288]"
              />
            </FormField>
            <FormField
              label={t("form.identity.city")}
              error={tenantErrors?.ville?.message}
              className="flex-1"
            >
              <input
                type="text"
                {...register(`individual_tenants.${arrayIndex}.ville`)}
                placeholder={t("form.placeholders.city")}
                disabled={disabled}
                className="h-[48px] px-[14px] py-[10px] border border-[#d0d5dd] rounded-[8px] focus:outline-none focus:ring-2 focus:ring-primary text-base text-[#757288]"
              />
            </FormField>
          </div>
        </div>

        {/* Correspondance par e-mail and État civil */}
        <div className="flex flex-col lg:flex-row gap-4 sm:gap-[16px] lg:gap-[24px]">
          {/* Correspondance par e-mail */}
          <div className="flex flex-col gap-4 sm:gap-[16px] flex-1">
            <div className="flex items-center gap-[5px]">
              <label className="font-bold text-off-black text-base">
                {t("form.identity.emailCorrespondence")}
              </label>
              <Asterisk className="w-[8px] h-[8px] text-off-red" />
            </div>
            <div className="flex flex-wrap gap-4 sm:gap-[24px]">
              <label className="flex items-center gap-[8px] cursor-pointer">
                <input
                  type="radio"
                  name={`individual_tenants.${arrayIndex}.correspondance_email`}
                  checked={watch(`individual_tenants.${arrayIndex}.correspondance_email`) === true}
                  onChange={() =>
                    setValue(`individual_tenants.${arrayIndex}.correspondance_email`, true, {
                      shouldValidate: true,
                    })
                  }
                  disabled={disabled}
                />
                <span className="text-off-black text-[16px]">{t("form.identity.yes")}</span>
              </label>
              <label className="flex items-center gap-[8px] cursor-pointer">
                <input
                  type="radio"
                  name={`individual_tenants.${arrayIndex}.correspondance_email`}
                  checked={watch(`individual_tenants.${arrayIndex}.correspondance_email`) === false}
                  onChange={() =>
                    setValue(`individual_tenants.${arrayIndex}.correspondance_email`, false, {
                      shouldValidate: true,
                    })
                  }
                  disabled={disabled}
                />
                <span className="text-off-black text-[16px]">{t("form.identity.no")}</span>
              </label>
            </div>
          </div>

          {/* État civil - hidden for PPE */}
          {!isPpe && (
            <div className="flex flex-col gap-4 sm:gap-[16px] flex-1">
              <div className="flex items-center gap-[5px]">
                <label className="font-bold text-off-black text-base">
                  {t("form.identity.maritalStatus")}
                </label>
                <Asterisk className="w-[8px] h-[8px] text-off-red" />
              </div>
              <div className="flex flex-wrap gap-4 sm:gap-[24px]">
                {["celibataire", "marie", "divorce", "veuf"].map((status) => (
                  <label key={status} className="flex items-center gap-[8px] cursor-pointer">
                    <input
                      type="radio"
                      {...register(`individual_tenants.${arrayIndex}.etat_civil`)}
                      value={status}
                      disabled={disabled}
                    />
                    <span className="text-off-black text-[16px]">
                      {t(`form.identity.maritalStatusOptions.${status}`)}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
